import { useCallback, useEffect, useState } from 'react'

import {
  fetchSubscriptionPlans,
  type Plan,
  purchaseSubscriptionPlan,
} from '../../api/subscriptionPlans'
import type { PlanPurchaseAction } from '../../lib/subscriptionPlanAction'
import { ThemeModal } from '../modal'
import { ThemeStagger, ThemeStaggerItem } from '../motion/ThemeMotion'

type Props = {
  open: boolean
  mode: PlanPurchaseAction
  angular: boolean
  onClose: () => void
  onPurchaseSuccess?: (message: string) => void
}

function formatPrice(n: number): string {
  if (!Number.isFinite(n)) return '—'
  if (Number.isInteger(n)) return n.toLocaleString('zh-CN')
  return n.toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

export function SubscriptionPlansModal({
  open,
  mode,
  angular,
  onClose,
  onPurchaseSuccess,
}: Props) {
  const cardRadius = angular ? 'rounded-none' : 'rounded-[var(--radius)]'
  const btnRadius = angular ? 'rounded-none' : 'rounded-xl'

  const [plans, setPlans] = useState<Plan[]>([])
  const [loading, setLoading] = useState(false)
  const [purchasingId, setPurchasingId] = useState<string | null>(null)
  const [message, setMessage] = useState<string | null>(null)

  const loadPlans = useCallback(async () => {
    setLoading(true)
    try {
      setPlans(await fetchSubscriptionPlans())
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    if (!open) {
      setMessage(null)
      setPurchasingId(null)
      return
    }
    void loadPlans()
  }, [open, loadPlans])

  const handlePurchase = async (plan: Plan) => {
    if (purchasingId) return
    setMessage(null)
    setPurchasingId(plan.id)
    try {
      const result = await purchaseSubscriptionPlan(plan.id)
      const successMessage = `${mode === 'purchase' ? '购买' : '升级'}成功：${result.planName}，赠送 ${result.bonusPointsCredited.toLocaleString('zh-CN')} 积分，到期 ${result.expiresAt}`
      setMessage(successMessage)
      onPurchaseSuccess?.(successMessage)
      await loadPlans()
    } catch {
      /* api 层已提示 */
    } finally {
      setPurchasingId(null)
    }
  }

  return (
    <ThemeModal
      open={open}
      onClose={onClose}
      title={mode === 'purchase' ? '购买套餐' : '升级套餐'}
      angular={angular}
      size="xl"
    >
      {message ? (
        <p className="mb-4 rounded-lg border border-line bg-surface-muted px-4 py-3 text-sm text-ink">
          {message}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-ink-soft">加载套餐中…</p>
      ) : plans.length === 0 ? (
        <p className="text-sm text-ink-soft">暂无可购套餐</p>
      ) : (
        <ThemeStagger className="grid w-full gap-4 sm:grid-cols-2 xl:grid-cols-2">
          {plans.map((plan) => {
            const featured = plan.recommended
            const atLimit =
              plan.limitMax != null &&
              plan.limitPurchased != null &&
              plan.limitPurchased >= plan.limitMax
            return (
              <ThemeStaggerItem key={plan.id}>
                <article
                  className={[
                    'relative flex flex-col border bg-panel shadow-card transition hover:shadow-float',
                    cardRadius,
                    featured
                      ? angular
                        ? 'border-2 border-accent ring-1 ring-accent/30'
                        : 'border-2 border-accent shadow-float ring-2 ring-accent/20'
                      : angular
                        ? 'border-2 border-line'
                        : 'border border-line',
                  ].join(' ')}
                >
                  {featured ? (
                    <div
                      className={[
                        'absolute -top-3 left-4 bg-accent px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-white sm:text-xs',
                        angular ? 'rounded-none' : 'rounded-full',
                      ].join(' ')}
                    >
                      推荐
                    </div>
                  ) : null}

                  <div className="flex flex-1 flex-col p-5 sm:p-6">
                    <h3 className="text-base font-semibold text-ink sm:text-lg">{plan.name}</h3>

                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-sm font-medium text-ink-soft">¥</span>
                      <span className="font-display text-3xl font-bold tracking-tight text-accent sm:text-4xl">
                        {formatPrice(plan.priceYuan)}
                      </span>
                    </div>

                    <p className="mt-2 text-xs text-ink-soft sm:text-sm">
                      {plan.durationLabel}
                      <span className="mx-1.5 text-line">·</span>
                      赠送{' '}
                      <span className="font-semibold text-ink">
                        {plan.bonusCredits.toLocaleString('zh-CN')}
                      </span>{' '}
                      积分
                    </p>

                    {plan.limitNote != null && plan.limitMax != null ? (
                      <div
                        className={[
                          'mt-3 inline-flex w-fit items-center gap-1 border border-line bg-surface-muted px-2.5 py-1 text-[11px] font-medium text-ink-soft',
                          angular ? 'rounded-none' : 'rounded-lg',
                        ].join(' ')}
                      >
                        <span className="text-ink">{plan.limitNote}</span>
                        <span>
                          {plan.limitPurchased ?? 0} / {plan.limitMax} 次
                        </span>
                      </div>
                    ) : null}

                    <p className="mt-4 text-xs leading-relaxed text-ink-soft sm:text-sm">
                      {plan.description}
                    </p>

                    <div className="mt-5 border-t border-line pt-4">
                      <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-soft">
                        包含能力
                      </p>
                      <ul className="mt-2 space-y-1.5 text-xs text-ink sm:text-sm">
                        {plan.features.map((feature) => (
                          <li key={feature} className="flex gap-2">
                            <span className="mt-0.5 shrink-0 text-accent" aria-hidden>
                              ✓
                            </span>
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div className="mt-6 flex-1" />

                    <button
                      type="button"
                      disabled={purchasingId === plan.id || atLimit}
                      onClick={() => void handlePurchase(plan)}
                      className={[
                        'w-full border border-transparent bg-accent py-3 text-sm font-semibold text-white transition hover:bg-accent-hover disabled:cursor-not-allowed disabled:opacity-50',
                        btnRadius,
                      ].join(' ')}
                    >
                      {atLimit
                        ? '已达限购'
                        : purchasingId === plan.id
                          ? '处理中…'
                          : mode === 'purchase'
                            ? '立即购买'
                            : '立即升级'}
                    </button>
                  </div>
                </article>
              </ThemeStaggerItem>
            )
          })}
        </ThemeStagger>
      )}
    </ThemeModal>
  )
}
